import { useEffect, useState } from "react";
import axios from "axios";
import { Shield, CheckCircle2, AlertTriangle, Circle, Loader2, FileDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABEL = {
  encryption: "Encryption",
  audit: "Audit & logging",
  "access-control": "Access control",
  "data-residency": "Data residency",
  retention: "Retention",
  consent: "Consent",
  "incident-response": "Incident response",
  "device-safety": "Device safety",
};

const STATUS_META = {
  pass:              { icon: CheckCircle2, cls: "text-green-600",  label: "Compliant",       badge: "default"     },
  partial:           { icon: AlertTriangle,cls: "text-amber-600",  label: "Partial",         badge: "secondary"   },
  "action-required": { icon: Circle,       cls: "text-destructive",label: "Action required", badge: "destructive" },
};

export default function AdminCompliance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/compliance/checklist`, {
          withCredentials: true,
        });
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div className="flex items-center gap-3 text-muted-foreground p-8" data-testid="compliance-loading">
      <Loader2 className="h-5 w-5 animate-spin" /> Loading compliance posture…
    </div>
  );
  if (error) return (
    <div className="text-destructive p-8" data-testid="compliance-error">Error: {error}</div>
  );
  if (!data) return null;

  const filtered = filter === "all"
    ? data.checklist
    : data.checklist.filter(r => r.jurisdiction === filter);

  const jurisdictions = ["all", ...Array.from(new Set(data.checklist.map(r => r.jurisdiction)))];

  const downloadPdf = async (j) => {
    try {
      const url = j === "all"
        ? `${API}/admin/compliance/report.pdf`
        : `${API}/admin/compliance/report.pdf?jurisdiction=${encodeURIComponent(j)}`;
      const res = await axios.get(url, { responseType: "blob", withCredentials: true });
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      const jSlug = j === "all" ? "all" : j.toLowerCase().replace(/-/g, "_");
      a.download = `physiogenic_compliance_${jSlug}.pdf`;
      document.body.appendChild(a);
      a.click();
      // Delay removal + URL revocation so the browser has time to start the
      // download (synchronous revokeObjectURL races with the download in some
      // browsers, cancelling the file).
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
    } catch (e) {
      alert("Failed to download PDF: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-compliance">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Shield className="h-7 w-7" />
          Compliance posture
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Live status of the platform&apos;s controls against every major healthcare
          regulation Physiogenic supports — HIPAA · HITECH · NIST · Medicare · NDIS ·
          NHS DSPT · DCB0129 / DCB0160 · EHDS · MDR · PHIPA · HIA. Snapshot
          generated {new Date(data.generated_at).toLocaleString()}.
        </p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3 max-w-xl">
        <Card className="text-center" data-testid="compliance-summary-pass">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-green-600">{data.summary.pass}</div>
            <div className="text-xs text-muted-foreground">Compliant</div>
          </CardContent>
        </Card>
        <Card className="text-center" data-testid="compliance-summary-partial">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-amber-600">{data.summary.partial}</div>
            <div className="text-xs text-muted-foreground">Partial</div>
          </CardContent>
        </Card>
        <Card className="text-center" data-testid="compliance-summary-action">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-destructive">{data.summary.action_required || 0}</div>
            <div className="text-xs text-muted-foreground">Action required</div>
          </CardContent>
        </Card>
      </div>

      {/* PDF export block */}
      <Card data-testid="compliance-pdf-export">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileDown className="h-5 w-5" />
            Export regulator-ready PDF
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Signed &amp; dated PDF for your DPO / CSO / Clinical Safety Officer to
            hand to a regulator during an on-site audit. Includes cover page,
            executive summary, full evidence table, and sign-off block.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="default" onClick={() => downloadPdf("all")}
                    data-testid="compliance-pdf-all" className="gap-2">
              <FileDown className="h-4 w-4" /> All jurisdictions
            </Button>
            {jurisdictions.filter(j => j !== "all").map(j => (
              <Button key={j} size="sm" variant="outline"
                      onClick={() => downloadPdf(j)}
                      data-testid={`compliance-pdf-${j}`} className="gap-2">
                <FileDown className="h-4 w-4" /> {j}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Jurisdiction filter */}
      <div className="flex flex-wrap gap-2" data-testid="compliance-filter">
        {jurisdictions.map(j => (
          <button key={j}
                  onClick={() => setFilter(j)}
                  data-testid={`compliance-filter-${j}`}
                  className={"px-3 py-1 rounded-md text-xs font-medium border transition " +
                    (filter === j ? "bg-primary text-primary-foreground border-primary" : "border-input hover:bg-accent")}>
            {j === "all" ? "All jurisdictions" : j}
          </button>
        ))}
      </div>

      {/* Checklist */}
      <div className="space-y-2" data-testid="compliance-list">
        {filtered.map((row) => {
          const meta = STATUS_META[row.status] || STATUS_META.pass;
          const Icon = meta.icon;
          return (
            <Card key={row.key} data-testid={`compliance-row-${row.key}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-start gap-3">
                  <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${meta.cls}`} />
                  <div className="flex-1">
                    <div>{row.regulation}</div>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{row.jurisdiction}</Badge>
                      <Badge variant="outline" className="text-[10px]">{CATEGORY_LABEL[row.category] || row.category}</Badge>
                      <Badge variant={meta.badge} className="text-[10px]">{meta.label}</Badge>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pl-11 text-xs space-y-1">
                <div className="text-muted-foreground">
                  <span className="font-semibold">Evidence: </span>
                  {row.evidence}
                </div>
                {row.owner_action && row.owner_action.length > 0 && (
                  <div className="text-amber-700">
                    <span className="font-semibold">Your action: </span>
                    <ul className="list-disc list-inside">
                      {row.owner_action.map((a, i) => (<li key={i}>{a}</li>))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
